import { MarketingFollowupBody } from './message-body/marketing-followup/MarketingFollowupBody';
/**
 * @file: MessageBody.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Per-type rich content dispatch for the feed thread timeline. The
 * `descriptorFor` helper (used by the inbox preview) returns a generic
 * title/subtitle; the per-type body components under `./message-body/`
 * go further, pulling the most informative fields out of each payload
 * — diff tables for order edits, vehicle + plan cards for maintenance
 * reminders, scored sub-scores for customer comments, etc.
 *
 * This file is just the exhaustive switch — each body lives in its
 * own file so the per-type render stays focused and reviewable.
 */

import { FeedInboxItem, FeedMessagePayload } from '@euler/model/feed';
import { FeedMessageType } from '@euler/model/feed/FeedMessageType';
import { FC, memo } from 'react';
import { CampaignRewardDepositedBody } from './message-body/CampaignRewardDepositedBody';
import { CustomerCommentPostedBody } from './message-body/CustomerCommentPostedBody';
import { CustomerViewedReportBody } from './message-body/CustomerViewedReportBody';
import { GlobalMessageBody } from './message-body/GlobalMessageBody';
import { LeadRemindBody } from './message-body/LeadRemindBody';
import { MaintenancePlanRemindBody } from './message-body/MaintenancePlanRemindBody';
import { MaintenancePlanSettingsChangedBody } from './message-body/MaintenancePlanSettingsChangedBody';
import { MaintenancePlanUpdatedBody } from './message-body/MaintenancePlanUpdatedBody';
import { MemberAccountWithdrawBody } from './message-body/MemberAccountWithdrawBody';
import { NewOrderBody } from './message-body/NewOrderBody';
import { OrderInfoUpdatedBody } from './message-body/OrderInfoUpdatedBody';
import { ProductReferralRewardDepositedBody } from './message-body/ProductReferralRewardDepositedBody';
import { ReportSharedBody } from './message-body/ReportSharedBody';
import { ReworkRequestBody } from './message-body/ReworkRequestBody';
import { SystemAlertBody } from './message-body/SystemAlertBody';
import { TaskAssignedBody } from './message-body/TaskAssignedBody';
import { TaskFlowStatusUpdateBody } from './message-body/TaskFlowStatusUpdateBody';
import { TaskStatusUpdateBody } from './message-body/TaskStatusUpdateBody';

export const MessageBody: FC<{
  payload: FeedMessagePayload;
  /** Author of this feed event — used by a few bodies (e.g.
   * TaskAssigned) to phrase the title as "X claimed" vs "X assigned to
   * Y" by comparing the actor against the payload's responsible party. */
  author?: FeedInboxItem['author'];
  /** Recipient-row id + read state — used by manual-read bodies
   * (maintenance-plan reminders) to render a "mark read" affordance. */
  messageId?: string;
  readAt?: string | null;
  onMarkRead?: (recipientId: string) => void;
  /** Convert-to-lead bundle done — mark all bundled reminders read. */
  onConvertedToLead?: (planDetailIds: number[]) => void;
}> = memo(
  ({ payload, author, messageId, readAt, onMarkRead, onConvertedToLead }) => {
    switch (payload.type) {
      case FeedMessageType.OrgNewOrder:
        return <NewOrderBody p={payload} />;
      case FeedMessageType.OrgOrderInfoUpdated:
        return <OrderInfoUpdatedBody p={payload} />;
      case FeedMessageType.OrgTaskAssigned:
        return <TaskAssignedBody p={payload} author={author} />;
      case FeedMessageType.OrgTaskStatusUpdate:
        return <TaskStatusUpdateBody p={payload} />;
      case FeedMessageType.OrgTaskFlowStatusUpdate:
        return <TaskFlowStatusUpdateBody p={payload} />;
      case FeedMessageType.OrgReworkRequest:
        return <ReworkRequestBody p={payload} />;
      case FeedMessageType.OrgReportShared:
        return <ReportSharedBody p={payload} />;
      case FeedMessageType.OrgCustomerViewedReport:
        return <CustomerViewedReportBody p={payload} />;
      case FeedMessageType.OrgMaintenancePlanUpdated:
        return <MaintenancePlanUpdatedBody p={payload} />;
      case FeedMessageType.OrgMaintenancePlanSettingsChanged:
        return <MaintenancePlanSettingsChangedBody p={payload} />;
      case FeedMessageType.OrgMaintenancePlanRemind:
        return (
          <MaintenancePlanRemindBody
            p={payload}
            messageId={messageId}
            readAt={readAt}
            onMarkRead={onMarkRead}
            onConvertedToLead={onConvertedToLead}
          />
        );
      case FeedMessageType.OrgMarketingFollowup:
        return (
          <MarketingFollowupBody
            p={payload}
            messageId={messageId}
            readAt={readAt}
            onMarkRead={onMarkRead}
          />
        );
      case FeedMessageType.OrgLeadRemind:
        return (
          <LeadRemindBody
            p={payload}
            messageId={messageId}
            readAt={readAt}
            onMarkRead={onMarkRead}
          />
        );
      case FeedMessageType.OrgCustomerCommentPosted:
        return <CustomerCommentPostedBody p={payload} />;
      case FeedMessageType.OrgProductReferralRewardDeposited:
        return <ProductReferralRewardDepositedBody p={payload} />;
      case FeedMessageType.OrgCampaignRewardDeposited:
        return <CampaignRewardDepositedBody p={payload} />;
      case FeedMessageType.OrgMemberAccountWithdraw:
        return <MemberAccountWithdrawBody p={payload} />;
      case FeedMessageType.OrgSystemAlert:
        return <SystemAlertBody p={payload} />;
      case FeedMessageType.OrgGlobalMessage:
        return <GlobalMessageBody p={payload} />;
      default: {
        const exhaustive: never = payload;
        void exhaustive;
        return null;
      }
    }
  },
);
